// State registry — the 7 chapters of YPO Southern 7.
// Shape per spec/tech-arch.md §2.2.
//
// `as const satisfies Record<string, State>` is load-bearing: it gives each
// state a literal type (so `states.alabama.name` is `'Alabama'`, not `string`)
// while still validating shape against the schema. `StateId = keyof typeof
// states` derives the union of all 7 ids; downstream Blocks (P-016 chapter-
// whatsapp, P-015 events-list) use it for exhaustive switches and option
// validation.
//
// **P-037.23 / D-131.** `whatsappGroups[]` URLs replaced with Banks-confirmed
// real chapter records. Typo normalization applied: per-state "Partners"
// → "Spouses/Partners" + NC's "NC Spouses/Partners" → "Spouses/Partners"
// (Banks's canonical naming). North Carolina retains 4 groups (no state-
// wide NC group — Banks confirmed the obsolete legacy entry is dropped).
// The `// TBD: from chapter records pre-launch` PLACEHOLDER URLs are gone;
// `accent` hex values remain TBD for PSD-verification post-launch.
//
// Post-D-131, `whatsappGroups[]` is consumed only as the seed for
// `chapter-whatsapp.defaultInputs.groupsByState` (per-Email-editable);
// WhatsAppRow no longer reads it directly — the Block passes the groups
// from `inputs.groupsByState[id]` so chapter staff can edit them per
// Email.
//
// State ids use kebab-case to match the project filename convention; multi-
// word ids (`'north-carolina'`, `'south-carolina'`) need bracket-notation
// access in code: `states['north-carolina']`.

export type WhatsAppGroup = { label: string; url: string }

export type State = {
  name: string
  flagImg: string
  accent: string
  whatsappGroups: WhatsAppGroup[]
}

export const states = {
  alabama: {
    name: 'Alabama',
    flagImg: '/flags/al.png',
    accent: '#a51e22', // TBD: PSD-verify accent pre-launch
    whatsappGroups: [
      { label: 'Members', url: 'https://chat.whatsapp.com/GJ0gIMT8urI7W76lU7lnyF' },
      { label: 'Spouses/Partners', url: 'https://chat.whatsapp.com/Kh1gsRQXzZg5kLK7jpmtrg' },
    ],
  },
  georgia: {
    name: 'Georgia',
    flagImg: '/flags/ga.png',
    accent: '#bf0a30', // TBD: PSD-verify accent pre-launch
    whatsappGroups: [
      { label: 'Members', url: 'https://chat.whatsapp.com/EF2qRaJ8Bs4HOeDeiw8gxk' },
      { label: 'Spouses/Partners', url: 'https://chat.whatsapp.com/EubvSRpKiS49kGxPgBLkD8' },
    ],
  },
  mississippi: {
    name: 'Mississippi',
    flagImg: '/flags/ms.png',
    accent: '#bf0a30', // TBD: PSD-verify accent pre-launch
    whatsappGroups: [
      { label: 'Members', url: 'https://chat.whatsapp.com/HKMLTxt1GakIkDignu9HtA' },
      { label: 'Spouses/Partners', url: 'https://chat.whatsapp.com/CwKRXaqKeZdEqgiOogI1x9' },
    ],
  },
  'north-carolina': {
    name: 'North Carolina',
    flagImg: '/flags/nc.png',
    accent: '#cd1041', // TBD: PSD-verify accent pre-launch
    whatsappGroups: [
      { label: 'Charlotte Members', url: 'https://chat.whatsapp.com/JBw4JPzQCqU3ByZ3jHudos' },
      { label: 'Triad Members', url: 'https://chat.whatsapp.com/CKXdaw0poNy95f2RkIN5ty' },
      { label: 'Triangle Members', url: 'https://chat.whatsapp.com/HWeh22N97rn9LGFoKDLrAu' },
      { label: 'Spouses/Partners', url: 'https://chat.whatsapp.com/Hw1WWT5WyGgGeLzPou3IpX' },
    ],
  },
  'south-carolina': {
    name: 'South Carolina',
    flagImg: '/flags/sc.png',
    accent: '#003478', // TBD: PSD-verify accent pre-launch
    whatsappGroups: [
      { label: 'Members', url: 'https://chat.whatsapp.com/GCDNhZJebPLGUIMQN4NVrh' },
      { label: 'Spouses/Partners', url: 'https://chat.whatsapp.com/LkpNJ10UzZ1FTIkobbEI09' },
    ],
  },
  tennessee: {
    name: 'Tennessee',
    flagImg: '/flags/tn.png',
    accent: '#002d65', // TBD: PSD-verify accent pre-launch
    whatsappGroups: [
      { label: 'Members', url: 'https://chat.whatsapp.com/JIcK0pAelP2IGdqbyQt3vi' },
      { label: 'Spouses/Partners', url: 'https://chat.whatsapp.com/GopIx9Yak8PFGFlqudAsvr' },
    ],
  },
  virginia: {
    name: 'Virginia',
    flagImg: '/flags/va.png',
    accent: '#0033a0', // TBD: PSD-verify accent pre-launch
    whatsappGroups: [
      { label: 'Members', url: 'https://chat.whatsapp.com/Io04V7PSIarA0aSZzGy4RM' },
      { label: 'Spouses/Partners', url: 'https://chat.whatsapp.com/ET6HycpESMECzGWTyIeOup' },
    ],
  },
} as const satisfies Record<string, State>

export type StateId = keyof typeof states

// Non-empty tuple of all state ids — the shape Zod's z.enum requires.
// Auto-tracks the registry: adding a state to `states` above propagates
// here (and to `StateId`) without further edits. Consumed by Block Options
// that take a state ref or list of state refs (events-list at P-015 per
// D-064; chapter-whatsapp at P-016). Extracted at the second use per
// /simplify recommendation: the cast is non-trivial (Zod-driven type-
// narrowing) and benefits from a single source of truth in the registry.
export const STATE_IDS = Object.keys(states) as [StateId, ...StateId[]]
